import { FullConfigurationWithExtraData } from '@/models/SWRCache';
import http from '@/services/HttpService';
import { AxiosResponse } from 'axios';
import { useSWRConfig } from 'swr';
import useSWRInfinite from 'swr/infinite';
import _ from 'underscore';

interface PaginatedResponse<T> {
    result: T;
    total: number;
}

const fetcher = (url: string) =>
    http.get(url).then((response: AxiosResponse) => {
        return {
            result: response.data,
            total: parseInt(response.headers.total),
        };
    });

/*
Exemplo de uso:
const getKey = (index: number) => {
    const limit: number = 3;
    const offset: number = index * limit;
    return `cast/searches/contents?offset=${offset}&limit=${limit}`;
};
const { data, hasError, loading, total, loadMore } = useGetPaginated<ContentStudentView>(getKey);
 */

interface Props {
    revalidateOnMount?: boolean;
}

export const useGetPaginated = <T>(getKey: (index: number) => string | null, configuration?: Props) => {
    const { cache } = useSWRConfig() as FullConfigurationWithExtraData;
    const { data, error, size, setSize, mutate } = useSWRInfinite<PaginatedResponse<T>>(getKey, fetcher, {
        shouldRetryOnError: false,
        revalidateOnReconnect: false,
        revalidateOnFocus: false,
        revalidateIfStale: false,
        revalidateOnMount: configuration?.revalidateOnMount,
    });

    const isLoadingInitialData: boolean = !data && !error;
    const isLoading: boolean = Boolean(isLoadingInitialData || (size > 0 && data && typeof data[size - 1] === 'undefined'));

    const total: number = data && data.length ? data[0].total : -1;
    const result: T[] = _.chain(data)
        .pluck('result')
        .flatten()
        .value() as T[];

    const loadMore = (): void => {
        setSize(size + 1);
    };

    const revalidateResult = (): void => {
        mutate(data);
    };

    const clear = (keyToRemove: string) => {
        if (keyToRemove) {
            for (const key of Array.from(cache.keys())) {
                if (String(key).endsWith(keyToRemove)) {
                    cache.delete(key);
                }
            }
        }
    };

    return {
        originalData: data,
        data: result,
        hasError: error,
        loading: isLoading,
        total,
        clear,
        mutate,
        loadMore,
        revalidateResult,
    };
};
